import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { storeUserInfo, selectUserInfo } from 'redux/userInfo/userInfoSlice';
import {
	setEnabledAppsLoading,
	storeEnabledApps,
	setEnabledAppsError,
} from 'redux/enabledApps/enabledAppsSlice';
import getUserInfoApi from 'api/login/getUserInfoApi';
import { getEnabledApps } from 'api/admin';
import convertUserInfo from 'libs/userInfo/convertUserInfo';
import { UserData } from 'types/userInfoTypes';

interface UseUserDataResult {
	userData: UserData | null;
	isLoading: boolean;
}

/**
 * @async
 * @function useUserData
 * @desc Fetches and manages user data using the API and Redux.
 * @param {boolean} skipFetch - If true, skips fetching user data (for unauthenticated routes)
 * @returns {UseUserDataResult} User data and loading state.
 */
function useUserData(skipFetch: boolean = false): UseUserDataResult {
	const dispatch = useDispatch();
	const userData = useSelector(selectUserInfo)?.userInfo as UserData | null;
	const [isLoading, setIsLoading] = useState<boolean>(!skipFetch);

	useEffect(() => {
		// Skip fetching if this is an unauthenticated route
		if (skipFetch) {
			setIsLoading(false);
			return;
		}

		const fetchUserData = async () => {
			try {
				setIsLoading(true);
				const data = await getUserInfoApi();
				const processedData: UserData = await convertUserInfo(data);
				dispatch(storeUserInfo(processedData));
			} catch (error) {
				console.error('Failed to fetch user data:', error);
			} finally {
				setIsLoading(false);
			}
		};

		if (userData?.userId === '') {
			fetchUserData();
		} else {
			setIsLoading(false);
		}
	}, [dispatch, userData, skipFetch]);

	// Load the instance app-enablement map once the user is authenticated.
	// Kept separate from the user fetch above so it runs regardless of how the
	// user got into Redux (fresh fetch vs. already populated after login).
	// Otherwise, navigating to a gated route post-login skips the fetch and the
	// AppEnabledGuard spins forever until a hard refresh.
	useEffect(() => {
		if (skipFetch) return;
		if (!userData?.userId) return;

		const fetchEnabledApps = async () => {
			dispatch(setEnabledAppsLoading());
			try {
				const enabledApps = await getEnabledApps();
				dispatch(storeEnabledApps(enabledApps));
			} catch (error) {
				console.error('Failed to fetch enabled apps:', error);
				dispatch(setEnabledAppsError());
			}
		};

		fetchEnabledApps();
	}, [dispatch, userData?.userId, skipFetch]);

	return { userData, isLoading };
}

export default useUserData;
